import { defaultQueryClient, clearPersistStorage } from '@shared/query-client';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('session-teardown');

/**
 * Longest we'll hold a logout redirect waiting on the IndexedDB purge.
 *
 * The purge is best-effort: if IndexedDB is slow, blocked by another tab's
 * open connection, or unavailable entirely (Safari private browsing), the
 * user must still get logged out and redirected. Two seconds is far above a
 * normal purge and far below "the app looks broken".
 */
const PURGE_TIMEOUT_MS = 2000;

/**
 * Drops every cached server response belonging to the session being torn down
 * — both the in-memory TanStack Query cache and the per-query IndexedDB
 * entries the persister wrote.
 *
 * Why this exists: `clearTokens()`/`logoutUserJwt()` used to clear only
 * localStorage. On the paths that redirect, the full page load happened to
 * destroy the in-memory cache, which masked the gap; on the three
 * `clearTokens()` calls in `cognito.ts` that pass no redirect, the app keeps
 * running with the previous session's articles and issues still cached. The
 * persisted IndexedDB entries survived even a reload, since they're keyed by
 * query hash and restored on the next boot.
 *
 * Whole-cache teardown rather than a targeted eviction is deliberate, and
 * matches what the call sites that already got this right do
 * (`magshop/auth/auth.hooks.tsx`'s `invalidateQueries()` + `removeQueries()`,
 * `features/login/ui/logout.tsx`'s `clearPersistStorage()`): there's no
 * per-query record of which responses were authenticated, so anything short
 * of clearing everything would be guesswork.
 *
 * Never rejects — callers chain a redirect onto it, and a failed cache purge
 * must not strand a user on a logged-out page.
 */
export function clearSessionCache(): Promise<void> {
	// Synchronous, so the no-redirect callers are covered even if the async
	// purge below never settles.
	try {
		defaultQueryClient.clear();
	} catch (err) {
		logger.error(err);
	}

	return Promise.race([
		clearPersistStorage(),
		new Promise<void>((resolve) => setTimeout(resolve, PURGE_TIMEOUT_MS)),
	])
		.then(() => undefined)
		.catch((err) => {
			logger.error(err);
		});
}
